import { useState } from "react";
import type { AxiosError } from "axios";
import { useNavigate } from "react-router-dom";
import { 
  useCohorts, 
  useCreateCohort, 
  useUpdateCohort, 
  useDeleteCohort,
  useAddStudentToCohort,
  useRemoveStudentFromCohort,
  useAvailableStudents,
  type Cohort,
  type StudentListItem,
} from "@/lib/api/students";
import { useCurrentUser } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Users, Plus, Pencil, Trash2, UserPlus, UserMinus, Loader2, X } from "lucide-react";

const getCurrentAcademicYear = () => {
  const now = new Date();
  const year = now.getFullYear();
  if (now.getMonth() >= 8) {
    return `${year}-${year + 1}`;
  }
  return `${year - 1}-${year}`;
};

const ageBands = [
  { value: "Band 1", label: "Band 1 (Ages 8-11)" },
  { value: "Band 2", label: "Band 2 (Ages 12-14)" },
  { value: "Band 3", label: "Band 3 (Ages 15-18)" },
];

export default function CohortManagementPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { data: user } = useCurrentUser();
  
  const { data: cohorts, isLoading: cohortsLoading } = useCohorts();
  
  const createCohort = useCreateCohort();
  const updateCohort = useUpdateCohort();
  const deleteCohort = useDeleteCohort();
  const addStudent = useAddStudentToCohort();
  const removeStudent = useRemoveStudentFromCohort();

  const [selectedCohort, setSelectedCohort] = useState<Cohort | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddStudentDialogOpen, setIsAddStudentDialogOpen] = useState(false);
  const [isViewStudentsDialogOpen, setIsViewStudentsDialogOpen] = useState(false);
  const [academicYear, setAcademicYear] = useState(getCurrentAcademicYear());
  
  const [formData, setFormData] = useState({
    name: "",
age_band: "",
    academic_year: getCurrentAcademicYear(),
  });

  const resetForm = () => {
    setFormData({ name: "", age_band: "", academic_year: getCurrentAcademicYear() });
  };

  const handleCreateCohort = async () => {
    if (!formData.name || !formData.age_band) {
      toast({ title: "Error", description: "Please fill in all required fields", variant: "destructive" });
      return;
    }

    console.log("Creating cohort with data:", JSON.stringify(formData));

    try {
      await createCohort.mutateAsync(formData);
      toast({ title: "Success", description: "Cohort created successfully" });
      setIsCreateDialogOpen(false);
      resetForm();
    } catch (error) {
      toast({ title: "Error", description: "Failed to create cohort", variant: "destructive" });
    }
  };

  const handleUpdateCohort = async () => {
    if (!selectedCohort || !formData.name || !formData.age_band) return;

    try {
      await updateCohort.mutateAsync({ id: selectedCohort.id, data: formData });
      toast({ title: "Success", description: "Cohort updated successfully" });
      setIsEditDialogOpen(false);
      setSelectedCohort(null);
      resetForm();
    } catch (error) {
      toast({ title: "Error", description: "Failed to update cohort", variant: "destructive" });
    }
  };

  const handleDeleteCohort = async (id: string) => {
    if (!confirm("Are you sure you want to delete this cohort? Students will be unenrolled.")) return;
    
    try {
      await deleteCohort.mutateAsync(id);
      toast({ title: "Success", description: "Cohort deleted successfully" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to delete cohort", variant: "destructive" });
    }
  };

  const handleAddStudent = async (studentId: string) => {
    if (!selectedCohort) return;

    try {
      await addStudent.mutateAsync({ 
        cohortId: selectedCohort.id, 
        data: { student_id: studentId } 
      });
      toast({ title: "Success", description: "Student added to cohort" });
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      toast({ 
        title: "Error", 
        description: err?.response?.data?.error || "Failed to add student", 
        variant: "destructive" 
      });
    }
  };

  const handleRemoveStudent = async (studentId: string) => {
    if (!selectedCohort) return;

    try {
      await removeStudent.mutateAsync({ 
        cohortId: selectedCohort.id, 
        data: { student_id: studentId } 
      });
      toast({ title: "Success", description: "Student removed from cohort" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to remove student", variant: "destructive" });
    }
  };

  const openEditDialog = (cohort: Cohort) => {
    setSelectedCohort(cohort);
    setFormData({
      name: cohort.name,
      age_band: cohort.year_group, // Using year_group field to store age_band for scoring
      academic_year: cohort.academic_year,
    });
    setIsEditDialogOpen(true);
  };

  const openAddStudentDialog = (cohort: Cohort) => {
    setSelectedCohort(cohort);
    setIsAddStudentDialogOpen(true);
  };

  const openViewStudentsDialog = (cohort: Cohort) => {
    setSelectedCohort(cohort);
    setIsViewStudentsDialogOpen(true);
  };

  return (
    <AppLayout role="teacher">
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-foreground">Cohort Management</h1>
            <p className="text-muted-foreground">Manage your cohorts and student enrollment</p>
          </div>
          <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button>
                <Plus className="w-4 h-4 mr-2" />
                Create Cohort
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Create New Cohort</DialogTitle>
                <DialogDescription>Create a new cohort for your students</DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Cohort Name *</Label>
                  <Input
                    id="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., 7A, Year 8 English"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="age_band">Age Band *</Label>
                  <Select
                    value={formData.age_band}
                    onValueChange={(v) => setFormData({ ...formData, age_band: v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select age band" />
                    </SelectTrigger>
                    <SelectContent>
                      {ageBands.map((ab) => (
                        <SelectItem key={ab.value} value={ab.value}>{ab.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="academic_year">Academic Year</Label>
                  <Input
                    id="academic_year"
                    value={formData.academic_year}
                    onChange={(e) => setFormData({ ...formData, academic_year: e.target.value })}
                    placeholder="e.g., 2025-2026"
                  />
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleCreateCohort} disabled={createCohort.isPending}>
                  {createCohort.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Create Cohort
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>

        {cohortsLoading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : !cohorts?.length ? (
          <Card>
            <CardContent className="flex flex-col items-center justify-center py-12">
              <Users className="w-12 h-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium mb-2">No cohorts yet</h3>
              <p className="text-muted-foreground text-center mb-4">
                Create your first cohort to start organizing students
              </p>
              <Button onClick={() => setIsCreateDialogOpen(true)}>
                <Plus className="w-4 h-4 mr-2" />
                Create Cohort
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {cohorts.map((cohort) => (
              <Card key={cohort.id} className="hover:border-primary transition-colors">
                <CardHeader className="pb-3">
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">{cohort.name}</CardTitle>
                      <CardDescription>{cohort.year_group} · {cohort.academic_year}</CardDescription>
                    </div>
                    <Badge variant="secondary">
                      {cohort.student_count} students
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => openViewStudentsDialog(cohort)}
                    >
                      <Users className="w-4 h-4 mr-1" />
                      View
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="flex-1"
                      onClick={() => openAddStudentDialog(cohort)}
                    >
                      <UserPlus className="w-4 h-4 mr-1" />
                      Add
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => openEditDialog(cohort)}
                    >
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button 
                      variant="ghost" 
                      size="sm"
                      onClick={() => handleDeleteCohort(cohort.id)}
                    >
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Cohort Dialog */}
        <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Edit Cohort</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="edit_name">Cohort Name *</Label>
                <Input
                  id="edit_name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit_age_band">Age Band *</Label>
                <Select
                  value={formData.age_band}
                  onValueChange={(v) => setFormData({ ...formData, age_band: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ageBands.map((ab) => (
                      <SelectItem key={ab.value} value={ab.value}>{ab.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsEditDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleUpdateCohort} disabled={updateCohort.isPending}>
                {updateCohort.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Add Student Dialog */}
        <Dialog open={isAddStudentDialogOpen} onOpenChange={setIsAddStudentDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Add Students to {selectedCohort?.name}</DialogTitle>
              <DialogDescription>
                Select students to add to this cohort
              </DialogDescription>
            </DialogHeader>
            {selectedCohort && (
              <AddStudentToCohortForm 
                cohortId={selectedCohort.id} 
                onAdd={handleAddStudent}
                onClose={() => setIsAddStudentDialogOpen(false)}
              />
            )}
          </DialogContent>
        </Dialog>

        {/* View Students Dialog */}
        <Dialog open={isViewStudentsDialogOpen} onOpenChange={setIsViewStudentsDialogOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Students in {selectedCohort?.name}</DialogTitle>
            </DialogHeader>
            {selectedCohort && (
              <ViewCohortStudents 
                cohort={selectedCohort} 
                onRemove={handleRemoveStudent}
              />
            )}
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>
  );
}

function AddStudentToCohortForm({ 
  cohortId, 
  onAdd, 
  onClose 
}: { 
  cohortId: string; 
  onAdd: (studentId: string) => void; 
  onClose: () => void;
}) {
  const yearGroups = [
    { value: 'Reception', label: 'Reception' },
    { value: '1', label: 'Year 1' },
    { value: '2', label: 'Year 2' },
    { value: '3', label: 'Year 3' },
    { value: '4', label: 'Year 4' },
    { value: '5', label: 'Year 5' },
    { value: '6', label: 'Year 6' },
    { value: '7', label: 'Year 7' },
    { value: '8', label: 'Year 8' },
    { value: '9', label: 'Year 9' },
    { value: '10', label: 'Year 10' },
    { value: '11', label: 'Year 11' },
    { value: '12', label: 'Year 12' },
    { value: '13', label: 'Year 13' },
  ];

  const [yearFilter, setYearFilter] = useState<string>("all");
  const { data: availableStudents, isLoading } = useAvailableStudents(cohortId, yearFilter === "all" ? undefined : yearFilter);
  const [selectedStudent, setSelectedStudent] = useState<string>("");

  const filteredStudents = yearFilter === "all" 
    ? availableStudents 
    : availableStudents?.filter(s => s.year_group?.toString() === yearFilter);

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>Filter by Year Group</Label>
        <Select value={yearFilter} onValueChange={setYearFilter}>
          <SelectTrigger>
            <SelectValue placeholder="All years" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All years</SelectItem>
            {yearGroups.map((yg) => (
              <SelectItem key={yg.value} value={yg.value}>{yg.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="space-y-2">
        <Label>Select Student</Label>
        {isLoading ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : !filteredStudents?.length ? (
          <p className="text-sm text-muted-foreground">No available students</p>
        ) : (
          <Select value={selectedStudent} onValueChange={setSelectedStudent}>
            <SelectTrigger>
              <SelectValue placeholder="Select a student" />
            </SelectTrigger>
            <SelectContent>
              {filteredStudents.map((student) => (
                <SelectItem key={student.id} value={student.id}>
                  {student.full_name} {student.year_group ? `(Year ${student.year_group})` : ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <DialogFooter>
        <Button variant="outline" onClick={onClose}>Close</Button>
        <Button 
          onClick={() => { onAdd(selectedStudent); setSelectedStudent(""); }} 
          disabled={!selectedStudent}
        >
          Add Student
        </Button>
      </DialogFooter>
    </div>
  );
}

function ViewCohortStudents({ 
  cohort, 
  onRemove 
}: { 
  cohort: Cohort; 
  onRemove: (studentId: string) => void;
}) {
  const students = cohort.students_list || [];

  return (
    <div className="space-y-4">
      {students.length === 0 ? (
        <p className="text-center text-muted-foreground py-4">No students in this cohort</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Year</TableHead>
              <TableHead className="text-right">Action</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {students.map((student) => (
              <TableRow key={student.id}>
                <TableCell className="font-medium">{student.full_name}</TableCell>
                <TableCell>{student.year_group ? `Year ${student.year_group}` : '-'}</TableCell>
                <TableCell className="text-right">
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => onRemove(student.id)}
                  >
                    <UserMinus className="w-4 h-4 text-destructive" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
